import React, { useEffect } from 'react';
import {
  Calendar,
  BookOpen,
  AlertCircle,
  Users,
} from 'lucide-react';
import { GoGraph } from "react-icons/go";
import { MdOutlineArrowOutward } from "react-icons/md";
import { BsCheckCircle } from "react-icons/bs";
import { BsCardChecklist } from "react-icons/bs";
import useAdminDashboardStore from '@/stores/AdminStore/AdminDashboardStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import './SuperAdminDashboard.css';

interface StatCardProps {
  title: string;
  value: number;
  increase: string;
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, increase, icon }) => (
  <div className="s-admin-dashboard-page-stat-card">
    <div className="s-admin-dashboard-page-stat-header">
      <div>
        <div className="s-admin-dashboard-page-stat-title">{title}</div>
        <div className="s-admin-dashboard-page-stat-value">{value.toLocaleString()}</div>
      </div>
      <div className="s-admin-dashboard-page-stat-icon">{icon}</div>
    </div>
    <div className="s-admin-dashboard-page-stat-increase">+ {increase} Increased from Last week</div>
  </div>
);

const DashboardPage: React.FC = () => {
  const {
    stats,
    courses,
    tasks,
    learningHoursData,
    loadingStats,
    loadingCourses,
    loadingTasks,
    loadingLearningHours,
    errorStats,
    fetchStats,
    fetchCourses,
    fetchTasks,
    fetchLearningHoursData,
  } = useAdminDashboardStore();

  // Combine loading states for initial load check
  const loading = loadingStats || loadingCourses || loadingTasks || loadingLearningHours;
  const error = errorStats;

  // Lazy load data on mount
  useEffect(() => {
    if (!stats) {
      fetchStats();
    }
  }, [stats, fetchStats]);

  useEffect(() => {
    if (!courses) {
      fetchCourses();
    }
  }, [courses, fetchCourses]);

  useEffect(() => {
    if (!tasks) {
      fetchTasks();
    }
  }, [tasks, fetchTasks]);

  useEffect(() => {
    if (!learningHoursData) {
      fetchLearningHoursData();
    }
  }, [learningHoursData, fetchLearningHoursData]);

  const getIconByType = (iconType: string) => {
    switch (iconType) {
      case 'book':
        return <BookOpen size={24} />;
      case 'alert':
        return <AlertCircle size={24} />;
      case 'check':
        return <BsCheckCircle size={24} />;
      case 'users':
        return <Users size={24} />;
      default:
        return <BookOpen size={24} />;
    }
  };

  if (loading && !stats && !courses && !tasks && !learningHoursData) {
    return (
      <div className="s-admin-dashboard-page">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error && !stats && !courses && !tasks && !learningHoursData) {
    return (
      <div className="s-admin-dashboard-page">
        <ErrorDisplay
          title="Failed to Load Dashboard Data"
          message={error}
          type="error"
          showRetry
          onRetry={() => {
            fetchStats();
            fetchCourses();
            fetchTasks();
            fetchLearningHoursData();
          }}
        />
      </div>
    );
  }

  return (
    <div className="s-admin-dashboard-page">
      <div className="s-admin-dashboard-page-header">
        <div>
          <h1>Dashboard</h1>
          <p>Welcome back! Here's what happening with your learning platform.</p>
        </div>
        <div className="s-admin-dashboard-page-date-selector">
          <span style={{borderRight:"1px solid var(--color-border)",padding:"10px 14px"}}>Showing Date</span>
          <span className="s-admin-dashboard-page-date-button">
            23 Sep, 2025 - 28 Sep, 2025
            <Calendar size={16} />
          </span>
        </div>
      </div>

      <div className="s-admin-dashboard-page-stats-grid">
        {stats && stats.map((stat, index) => (
          <StatCard
            key={index}
            title={stat.title}
            value={stat.value}
            increase={stat.increase}
            icon={getIconByType(stat.iconType)}
          />
        ))}
      </div>

      <div className="s-admin-dashboard-page-analytics-banner">
        <div className="s-admin-dashboard-page-analytics-icon"><GoGraph /></div>
        <div>
          <div className="s-admin-dashboard-page-analytics-title">Analytics & Graphs</div>
          <div className="s-admin-dashboard-page-analytics-subtitle">Recommended for you</div>
        </div>
      </div>

      <div className="s-admin-dashboard-page-content-grid">
        {/* Learning Hours Card */}
        <div className="s-admin-dashboard-page-learning-hours-card">
          <div className="s-admin-dashboard-page-learning-hours-row">
            {/* Left Section: Chart */}
            <div className="s-admin-dashboard-page-chart-section">
              <div className="s-admin-dashboard-page-card-header">
                <h3>Learning Hours Trend</h3>
                <p>Time Employees spent in daily learnings</p>
              </div>
              <div className="s-admin-dashboard-page-chart-wrapper">
                <div className="s-admin-dashboard-page-chart-y-axis">
                  <span>40</span>
                  <span>30</span>
                  <span>20</span>
                  <span>10</span>
                </div>
                <div className="s-admin-dashboard-page-chart-area">
                  <svg viewBox="0 0 500 200" className="s-admin-dashboard-page-line-chart">
                    <polyline
                      points="0,180 100,140 200,150 300,100 400,20 500,10"
                      fill="none"
                      stroke="#ff8c42"
                      strokeWidth="3"
                    />
                    <circle cx="300" cy="100" r="5" fill="#ff8c42" stroke="white" strokeWidth="2" />
                    <line x1="300" y1="100" x2="300" y2="200" stroke="#ddd" strokeWidth="1" strokeDasharray="5,5" />
                  </svg>
                  <div className="s-admin-dashboard-page-chart-labels">
                    {learningHoursData && learningHoursData.map((item, i) => (
                      <span key={i}>{item.week}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Section: Date and Progress */}
            <div className="s-admin-dashboard-page-progress-section">
              <div className="s-admin-dashboard-page-date-button-small">
                June - 2025
                <Calendar size={14} />
              </div>

              <div className="s-admin-dashboard-page-progress-info">
                <div className="s-admin-dashboard-page-progress-icon"><MdOutlineArrowOutward /></div>
                <div>
                  <div className="s-admin-dashboard-page-progress-label">Progress</div>
                  <div className="s-admin-dashboard-page-progress-value">
                    68 Hours <span className="s-admin-dashboard-page-progress-percent">45.5% <MdOutlineArrowOutward /></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Courses Card */}
        <div className="s-admin-dashboard-page-top-courses-card">
          <h3>Top 5 Courses</h3>
          <p>Total Number of Registered Employees</p>
          <div className="s-admin-dashboard-page-courses-list">
            {courses && courses.map((course, i) => (
              <div key={i} className="s-admin-dashboard-page-course-item">
                <div className="s-admin-dashboard-page-course-stats">
                  <div className="s-admin-dashboard-page-course-name">{course.name}</div>
                  <span className="s-admin-dashboard-page-course-count">
                    {course.enrolled} of {course.total}
                  </span>
                </div>
                <div className="s-admin-dashboard-page-course-progress-bar">
                  <div
                    className="s-admin-dashboard-page-course-progress-fill"
                    style={{ width: `${(course.enrolled / course.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tasks Card */}
      <div className="s-admin-dashboard-page-tasks-card">
        <div className="s-admin-dashboard-page-tasks-header">
          <div className="s-admin-dashboard-page-tasks-header-left">
            <div className="s-admin-dashboard-page-tasks-icon"><BsCardChecklist /></div>
            <div>
              <h3>My Tasks</h3>
              <p>Overview of My Important Tasks</p>
            </div>
          </div>
          <button className="s-admin-dashboard-page-view-details-btn">
            View in Details <span><MdOutlineArrowOutward/></span>
          </button>
        </div>

        <table className="s-admin-dashboard-page-tasks-table">
          <thead>
            <tr>
              <th className="s-admin-dashboard-page-task-dot-column"></th>
              <th>Task Description</th>
              <th>Due Date</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {tasks && tasks.map((task, i) => (
              <tr key={i}>
                <td className="s-admin-dashboard-page-task-dot-cell">
                  <div
                    className={`dashboard-page-task-dot ${
                      task.status === 'Urgent'
                        ? 'dashboard-page-task-dot--urgent'
                        : 'dashboard-page-task-dot--pending'
                    }`}
                  />
                </td>
                <td>
                  <div className="s-admin-dashboard-page-task-description">
                    <div>
                      <div className="s-admin-dashboard-page-task-type">
                        {task.type}{' '}
                        <span
                          className={`dashboard-page-task-status dashboard-page-task-status--${task.status.toLowerCase()}`}
                        >
                          {task.status}
                        </span>
                      </div>
                      <div className="s-admin-dashboard-page-task-detail">{task.description}</div>
                    </div>
                  </div>
                </td>
                <td className="s-admin-dashboard-page-task-date">{task.dueDate}</td>
                <td className="s-admin-dashboard-page-task-action">
                  <button className="s-admin-dashboard-page-action-button">{task.action}</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default DashboardPage;
